import {
  Box,
  Button,
  Checkbox as MUICheckbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Stack
} from '@mui/material';
import useAuth from 'hooks/useAuth';
import moment from 'moment-timezone';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useMutation } from 'react-query';
import { useParams } from 'react-router';
import { NTCalendarEvent } from 'types/appointment';
import { getErrorMessage } from 'utils/stringUtils';
import DialogClose from 'views/components/buttons/DialogClose';
import BubbleColorPicker from 'views/components/inputs/BubbleColorPicker';
import Checkbox from 'views/components/inputs/Checkbox';
import DatePicker from 'views/components/inputs/DatePicker';
import FormContainer from 'views/components/inputs/FormContainer';
import MultiDatePicker from 'views/components/inputs/MultiDatePicker';
import RadioButtonGroup from 'views/components/inputs/RadioButtonGroup';
import Select from 'views/components/inputs/Select';
import TextField from 'views/components/inputs/TextField';
import TimePicker from 'views/components/inputs/TimePicker';
import BasicDialog from 'views/components/modals/BasicDialog';
import * as Yup from 'yup';

interface Props {
  event: NTCalendarEvent;
  onUpdateSuccess: () => void;
  onClose: () => void;
  events: NTCalendarEvent[];
}

type FormValues = any;

type REPEAT_OPTIONS = 'singleEvent' | 'recurringEvent' | 'multipleDates';

export default function EditBlockOffTimeSlot({ event, onUpdateSuccess, onClose, events }: Props) {
  const intl = useIntl();
  const { request, userId: id } = useAuth();
  const { userId } = useParams();

  const [updating, setUpdating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteFutureEvents, setDeleteFutureEvents] = useState(false);

  const [deleteEventOpen, setDeleteEventOpen] = useState(false);

  const validationSchema = Yup.object().shape({
    date: Yup.date().required(),
    startTime: Yup.date()
      .required(intl.formatMessage({ defaultMessage: 'Required' }))
      .nullable()
      .transform((curr, orig) => (orig === '' ? null : curr))
      .typeError(intl.formatMessage({ defaultMessage: 'Start time is not valid' })),
    endTime: Yup.date()
      .required(intl.formatMessage({ defaultMessage: 'Required' }))
      .nullable()
      .transform((curr, orig) => (orig === '' ? null : curr))
      .typeError(intl.formatMessage({ defaultMessage: 'End time is not valid' }))
      .min(Yup.ref('startTime'), intl.formatMessage({ defaultMessage: "End Time can't be before Start Time" })),
    description: Yup.string()
  });

  const { enqueueSnackbar } = useSnackbar();

  const updateTimeBlockMuatation = useMutation(
    (variables: any) => request.put(`/user/${userId ?? id}/availability/${event.event_id}`, variables),
    {
      onMutate() {
        setUpdating(true);
      },
      onSuccess() {
        enqueueSnackbar(intl.formatMessage({ defaultMessage: 'Time slot successfully edited' }), { variant: 'success' });
        onUpdateSuccess();
        onClose();
      },
      onError(e: any) {
        enqueueSnackbar(getErrorMessage(e), { variant: 'error' });
      },
      onSettled() {
        setUpdating(false);
      }
    }
  );

  const deleteTimeBlockMuatation = useMutation(
    () => request.delete(`/user/${userId ?? id}/availability/${event.event_id}`, { data: { deleteFutureEvents } }),
    {
      onMutate() {
        setDeleting(true);
      },
      onSettled() {
        setDeleting(false);
      }
    }
  );

  const handleDelete = useCallback(async () => {
    try {
      await deleteTimeBlockMuatation.mutateAsync();
      setDeleteEventOpen(false);
      setDeleteFutureEvents(false);
      enqueueSnackbar(intl.formatMessage({ defaultMessage: 'Blocked time slot successfully deleted' }), { variant: 'success' });
      onClose();
      onUpdateSuccess();
    } catch (e) {
      enqueueSnackbar(getErrorMessage(e), { variant: 'error' });
    }
  }, [deleteTimeBlockMuatation, enqueueSnackbar, intl, onClose, onUpdateSuccess]);

  const DeleteTimeBlockDialog = useCallback(
    () => (
      <BasicDialog
        open={deleteEventOpen}
        onClose={() => setDeleteEventOpen(false)}
        maxWidth="xs"
        title={intl.formatMessage({
          defaultMessage: 'Delete time block'
        })}
        actions={
          <>
            <Button variant="contained" onClick={() => setDeleteEventOpen(false)}>
              <FormattedMessage defaultMessage="Cancel" />
            </Button>
            <Button variant="contained" onClick={handleDelete} disabled={deleting}>
              <FormattedMessage defaultMessage="Delete" />
            </Button>
          </>
        }
      >
        <Box>
          <FormattedMessage defaultMessage={'Are you sure you want to delete this time block?'} />
        </Box>

        <FormControlLabel
          control={<MUICheckbox checked={deleteFutureEvents} onChange={(e) => setDeleteFutureEvents(e.target.checked)} />}
          label={intl.formatMessage({ defaultMessage: 'Delete Future Recurring Events' })}
        />
      </BasicDialog>
    ),
    [deleteFutureEvents, setDeleteFutureEvents, setDeleteEventOpen, deleteEventOpen, intl, handleDelete, deleting]
  );

  const onFormSubmit = useCallback(
    async ({ repeatOptions, date, multipleDates, startTime, endTime, updateRecurring, ...values }: FormValues) => {
      try {
        const updateRecurring = (repeatOptions as REPEAT_OPTIONS) === 'recurringEvent';
        const editMultipleDates = (repeatOptions as REPEAT_OPTIONS) === 'multipleDates';

        const startTimeStr = moment(startTime).format('HH:mm');
        const endTimeStr = moment(endTime).format('HH:mm');

        const data = {
          ...values,
          dates: editMultipleDates
            ? multipleDates?.map((d) => ({
                start: d.unix
                  ? moment(`${moment.unix(d.unix).format('YYYY-MM-DD')} ${startTimeStr}`).toISOString()
                  : moment(`${moment(d).format('YYYY-MM-DD')} ${startTimeStr}`).toISOString(),
                end: d.unix
                  ? moment(`${moment.unix(d.unix).format('YYYY-MM-DD')} ${endTimeStr}`).toISOString()
                  : moment(`${moment(d).format('YYYY-MM-DD')} ${endTimeStr}`).toISOString()
              })) ?? []
            : [
                {
                  start: moment(`${moment(date).format('YYYY-MM-DD')} ${moment(startTime).format('HH:mm')}`).toISOString(),
                  end: moment(`${moment(date).format('YYYY-MM-DD')} ${moment(endTime).format('HH:mm')}`).toISOString()
                }
              ],
          updateRecurring,
          repeatOptions
        };

        if (!data.dates.length) {
          enqueueSnackbar(intl.formatMessage({ defaultMessage: 'Date is required' }), { variant: 'error' });
          return;
        }

        await updateTimeBlockMuatation.mutateAsync(data);
      } catch (e: any) {
        enqueueSnackbar(getErrorMessage(e), { variant: 'error' });
      }
    },
    [updateTimeBlockMuatation, enqueueSnackbar, intl]
  );

  const initialValues = {
    bubbleColor: event.bubbleColor,
    date: new Date(event.start),
    startTime: new Date(event.start),
    endTime: new Date(event.end),
    label: event.label,
    description: event.description,
    fullDay: event.fullDay ?? false,
    syncWithExternalCalendar: event.syncWithExternalCalendar,
    updateRecurring: 'false',
    repeatOptions: 'singleEvent'
  };

  return (
    <>
      <DeleteTimeBlockDialog />
      <Dialog open onClose={onClose} maxWidth="sm" fullWidth sx={{ '& .MuiDialog-paper': { p: 0 } }}>
        <FormContainer validation={validationSchema} defaultValues={initialValues} onSuccess={onFormSubmit}>
          <DialogTitle>
            <FormattedMessage defaultMessage="Edit Block Off Time Slot" />
            <DialogClose onClose={onClose} />
          </DialogTitle>
          <DialogContent sx={{ p: 3 }}>
            <AppointmentInfoFields repeatId={event.repeatId} events={events} />
          </DialogContent>
          <Divider />
          <DialogActions sx={{ py: 2, px: 3 }}>
            <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="center" style={{ width: '100%' }}>
              <Button variant="contained" disabled={false} onClick={() => setDeleteEventOpen(true)}>
                <FormattedMessage defaultMessage="Delete" />
              </Button>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Button type="button" variant="outlined" onClick={onClose}>
                  <FormattedMessage defaultMessage="Cancel" />
                </Button>
                <Button type="submit" variant="contained" disabled={updating}>
                  <FormattedMessage defaultMessage="Update" />
                </Button>
              </Stack>
            </Stack>
          </DialogActions>
        </FormContainer>
      </Dialog>
    </>
  );
}

type AppointmentInfoFieldsProps = {
  repeatId?: string | null;
  events?: NTCalendarEvent[];
};

function AppointmentInfoFields({ repeatId, events }: AppointmentInfoFieldsProps) {
  const intl = useIntl();

  const { watch, setValue } = useFormContext();
  const fullDay = watch('fullDay');
  const repeatOptions = watch('repeatOptions');

  useEffect(() => {
    if (repeatId) {
      const repeatingAppointmentsDates: string[] = [];

      events?.forEach((event) => {
        if (event.repeatId === repeatId) {
          repeatingAppointmentsDates.push(event.start);
        }
      });

      setValue('multipleDates', repeatingAppointmentsDates);
    }
  }, [repeatId, setValue, events]);

  useEffect(() => {
    if (fullDay) {
      setValue('startTime', moment().startOf('day').toDate());
      setValue('endTime', moment().endOf('day').toDate());
    }
  }, [fullDay, setValue]);

  return (
    <Stack direction="column" spacing={3}>
      {!!repeatId && (
        <Box sx={(theme) => ({ p: 1, mb: 1, backgroundColor: '#e5e5e5' })}>
          <RadioButtonGroup
            name="repeatOptions"
            label={intl.formatMessage({ defaultMessage: 'Edit For' })}
            options={[
              { label: 'Single Event', id: 'singleEvent' },
              { label: 'All future recurring events', id: 'recurringEvent' },
              { label: 'Multiple Dates', id: 'multipleDates' }
            ]}
            row
          />
        </Box>
      )}

      <Stack direction="row" spacing={2} alignItems="center" pt={1}>
        {repeatOptions === 'multipleDates' ? (
          <MultiDatePicker name="multipleDates" label={intl.formatMessage({ defaultMessage: 'Date' })} required />
        ) : (
          <DatePicker name="date" label={intl.formatMessage({ defaultMessage: 'Date' })} required />
        )}

        <Box minWidth={100}>
          <Checkbox name="fullDay" color="primary" label={intl.formatMessage({ defaultMessage: 'Full Day' })} />
        </Box>
      </Stack>

      <Stack direction="row" spacing={2} alignItems="center" sx={{ display: !fullDay ? 'block' : 'none' }}>
        <TimePicker name="startTime" label={intl.formatMessage({ defaultMessage: 'Start Time' })} sx={{ flex: 1 }} required />
        <TimePicker name="endTime" label={intl.formatMessage({ defaultMessage: 'End Time' })} sx={{ flex: 1 }} required />
      </Stack>

      <TextField name="description" label={intl.formatMessage({ defaultMessage: 'Description, if any' })} fullWidth multiline rows={5} />

      <Stack direction="row" spacing={2} alignItems="center">
        <BubbleColorPicker name="bubbleColor" label={<FormattedMessage defaultMessage="Bubble Color" />} />

        <Select
          name="label"
          label={intl.formatMessage({ defaultMessage: 'Label' })}
          options={['Personal', 'Break', 'Lunch', 'Breakfast', 'Dinner', 'Other']}
        />
      </Stack>

      {/* {!!repeatId && (
        <Stack direction="row" sx={{ mx: 3 }} spacing={5} justifyContent="flex-start">
          <RadioButtonGroup
            name="updateRecurring"
            label="Edit For"
            options={[
              { label: 'This event only', id: 'false' },
              { label: 'All future recurring events', id: 'true' }
            ]}
            row
          />
        </Stack>
      )} */}
    </Stack>
  );
}
